import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { brandInfo, faqs } from '@/mocks/data';
import { useState } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const urlSearchParams = new URLSearchParams();
      formData.forEach((value, key) => {
        urlSearchParams.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9mtt28bdkpk99j7ge0g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlSearchParams.toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch (err) {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.slice(0, 4).map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 小蓝视频',
          url: 'https://www.xiaolanvideo.com/about',
          description: '小蓝视频品牌介绍、口碑评价及联系方式。汇聚香蕉视频、糖豆视频等平台高清免费在线观看。',
        }}
      />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />

      <main className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="px-4 md:px-6 py-10 md:py-16 bg-background-100">
          <div className="max-w-5xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-2xl bg-primary-500 mb-6">
              <i className="ri-play-circle-fill text-4xl text-white"></i>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading text-foreground-900 mb-4">
              关于 {brandInfo.name}
            </h1>
            <p className="text-lg text-foreground-500 mb-2">{brandInfo.slogan}</p>
            <p className="text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed mt-6">
              {brandInfo.introduction}
            </p>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-10">
              为什么选择小蓝视频
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {brandInfo.reputation.map((item, index) => (
                <div
                  key={index}
                  className="bg-background-50 border border-background-200/70 rounded-xl p-6 text-center hover:border-primary-200 transition-all duration-300"
                >
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-accent-100 text-accent-600">
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-800 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-foreground-400">电子邮箱</p>
                      <a href={`mailto:${brandInfo.contact.email}`} className="text-foreground-800 font-medium hover:text-primary-500 transition-colors">
                        {brandInfo.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-foreground-400">客服电话</p>
                      <p className="text-foreground-800 font-medium">{brandInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm text-foreground-400">公司地址</p>
                      <p className="text-foreground-800 font-medium">{brandInfo.contact.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-6">
                  留言反馈
                </h2>
                {formStatus === 'success' ? (
                  <div className="bg-accent-50 border border-accent-200 rounded-xl p-8 text-center">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-800">提交成功！</h3>
                    <p className="text-sm text-foreground-500 mt-1">感谢您的留言，我们会在24小时内回复。</p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="mt-4 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      继续留言
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                    <input
                      type="text"
                      name="website_alt"
                      className="form-anti-spam"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500">{formError}</p>
                    )}
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      提交留言
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}